import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import BhtLogo from "@/components/common/BhtLogo";
import { Link } from "react-router-dom";

/**
 * Public legal pages. Reachable even during Stealth Launch mode.
 * The content below is a first draft — mark clearly as such and route it
 * through legal review before the public launch.
 */

function LegalShell({ title, updated, children, testId }) {
  const { i18n } = useTranslation();
  const lang = i18n.language || "en";
  const isNl = lang === "nl";
  return (
    <div className="relative min-h-screen bg-bht-void text-white" data-testid={testId}>
      <Helmet>
        <html lang={lang} />
        <title>{`${title} — BHT Solutions`}</title>
      </Helmet>
      <header className="border-b border-white/[0.06]">
        <div className="mx-auto flex max-w-[900px] items-center justify-between px-6 py-6 lg:px-8">
          <Link to={`/${lang}`} className="flex items-center gap-3">
            <BhtLogo className="h-8 w-8" />
            <span className="font-serif text-lg tracking-tight">BHT Solutions</span>
          </Link>
          <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-widest text-white/50">
            <Link to={`/${isNl ? "en" : "nl"}${window.location.pathname.replace(/^\/(en|nl)/, "")}`} className="hover:text-bht-gold" data-testid="legal-lang-switch">
              {isNl ? "EN" : "NL"}
            </Link>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[820px] px-6 py-16 lg:px-8">
        <div className="eyebrow">{isNl ? "Juridisch" : "Legal"}</div>
        <h1 className="mt-4 font-serif text-4xl leading-tight tracking-tight sm:text-5xl">{title}</h1>
        <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-white/40">
          {isNl ? "Laatst bijgewerkt" : "Last updated"}: {updated}
        </p>
        <div className="mt-3 rounded-xl border border-bht-gold/25 bg-bht-gold/[0.05] px-4 py-3 text-xs text-bht-gold/85">
          {isNl
            ? "Concept — dit document wordt herzien door onze juridische adviseurs voor de publieke lancering."
            : "Draft — this document is under legal review ahead of the public launch."}
        </div>
        <div className="prose prose-invert mt-12 max-w-none space-y-6 text-[15px] leading-relaxed text-white/75">
          {children}
        </div>
      </main>
      <footer className="border-t border-white/[0.06]">
        <div className="mx-auto flex max-w-[900px] flex-wrap items-center justify-between gap-3 px-6 py-6 text-xs text-white/45 lg:px-8">
          <span>© {new Date().getFullYear()} BHT Solutions</span>
          <div className="flex items-center gap-5">
            <Link to={`/${lang}/legal/privacy`} className="hover:text-bht-gold" data-testid="legal-footer-privacy">
              {isNl ? "Privacy" : "Privacy"}
            </Link>
            <Link to={`/${lang}/legal/terms`} className="hover:text-bht-gold" data-testid="legal-footer-terms">
              {isNl ? "Voorwaarden" : "Terms"}
            </Link>
            <Link to={`/${lang}/legal/cookies`} className="hover:text-bht-gold" data-testid="legal-footer-cookies">
              {isNl ? "Cookies" : "Cookies"}
            </Link>
            <a href="mailto:info@bhtsolutions.nl" className="hover:text-bht-gold">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

const H2 = ({ children }) => <h2 className="mt-10 font-serif text-2xl tracking-tight text-white">{children}</h2>;
const H3 = ({ children }) => <h3 className="mt-6 font-sans text-base font-semibold text-white/90">{children}</h3>;

// ---------- PRIVACY ----------
export function Privacy() {
  const { i18n } = useTranslation();
  const isNl = i18n.language === "nl";
  const title = isNl ? "Privacybeleid" : "Privacy Policy";
  const updated = "2026-02-01";
  return (
    <LegalShell title={title} updated={updated} testId="page-privacy">
      {isNl ? (
        <>
          <p>
            BHT Solutions ("wij", "ons") respecteert uw privacy. Dit beleid legt uit welke gegevens wij verzamelen via
            deze website, waarom, en welke rechten u heeft. Contact:{" "}
            <a href="mailto:info@bhtsolutions.nl" className="text-bht-gold">info@bhtsolutions.nl</a>.
          </p>
          <H2>Welke gegevens wij verzamelen</H2>
          <ul className="list-disc space-y-2 pl-5">
            <li><strong>Contact- en demo-formulieren:</strong> naam, e-mailadres, bedrijfsnaam, functie, propertytype, aantal kamers, bericht.</li>
            <li><strong>Launch List / nieuwsbrief:</strong> voornaam (optioneel), e-mailadres, taalvoorkeur, gekozen segment (Hotelier / Partner / Investor / Overig), tijdstempel en consent-status.</li>
            <li><strong>Preview-toegang:</strong> IP-adres en user-agent voor beveiliging en fraudepreventie. De ingevoerde toegangscode wordt <em>nooit</em> gelogd.</li>
            <li><strong>Server-logs:</strong> technische metadata (IP, tijd, statuscodes) voor beveiliging.</li>
          </ul>
          <H2>Nieuwsbrief en double opt-in</H2>
          <p>
            Wanneer u zich aanmeldt voor de Launch List, sturen wij een bevestigingsmail waarin u uw abonnement met één klik dient te bevestigen (double opt-in).
            Zonder bevestiging wordt uw e-mailadres niet actief gebruikt en verwijderen wij pending aanmeldingen na maximaal 90 dagen.
          </p>
          <H2>Verwerkers</H2>
          <ul className="list-disc space-y-2 pl-5">
            <li><strong>Resend</strong> — e-mailbezorging voor transactionele en marketingmails. Zij ontvangen uw e-mailadres, naam en de e-mailinhoud. Zie <a href="https://resend.com/privacy" className="text-bht-gold" target="_blank" rel="noreferrer">resend.com/privacy</a>.</li>
            <li><strong>MongoDB / hosting</strong> — versleutelde opslag van formulierdata binnen de EU.</li>
          </ul>
          <H2>Bewaartermijnen</H2>
          <ul className="list-disc space-y-2 pl-5">
            <li>Contact- en demo-inzendingen: 24 maanden vanaf laatste contactmoment.</li>
            <li>Bevestigde nieuwsbriefabonnees: totdat u zich uitschrijft.</li>
            <li>Pending (niet-bevestigde) aanmeldingen: 90 dagen.</li>
            <li>Preview-audit logs: 12 maanden.</li>
          </ul>
          <H2>Uw rechten</H2>
          <p>
            U heeft het recht op inzage, correctie, verwijdering, beperking en dataportabiliteit conform de AVG. Verstuur uw verzoek naar{" "}
            <a href="mailto:info@bhtsolutions.nl" className="text-bht-gold">info@bhtsolutions.nl</a>. Wij reageren binnen 30 dagen.
            U kunt ook een klacht indienen bij de Autoriteit Persoonsgegevens.
          </p>
          <H2>Cookies</H2>
          <p>
            Wij gebruiken uitsluitend functionele cookies die noodzakelijk zijn voor de werking van de website, waaronder een sessie-cookie voor
            preview-toegang. Zie ons <Link to={`/${i18n.language}/legal/cookies`} className="text-bht-gold">Cookiebeleid</Link>.
          </p>
          <H2>Internationale gegevensoverdracht</H2>
          <p>
            Onze primaire hosting bevindt zich binnen de EU. Onze e-mailverwerker Resend kan gegevens buiten de EU verwerken op basis van
            standaardcontractbepalingen (SCC's) zoals goedgekeurd door de Europese Commissie.
          </p>
          <H2>Analytics</H2>
          <p>
            Op dit moment gebruiken wij geen analytics-tracking op deze pagina's. Wanneer wij analytics inschakelen, updaten wij dit beleid.
          </p>
        </>
      ) : (
        <>
          <p>
            BHT Solutions ("we", "us") respects your privacy. This policy explains what personal data we collect through this website,
            why we do so, and what rights you have. Contact:{" "}
            <a href="mailto:info@bhtsolutions.nl" className="text-bht-gold">info@bhtsolutions.nl</a>.
          </p>
          <H2>Data we collect</H2>
          <ul className="list-disc space-y-2 pl-5">
            <li><strong>Contact & demo forms:</strong> name, email, company, role, property type, room count, message.</li>
            <li><strong>Launch List / newsletter:</strong> first name (optional), email, language, selected segment (Hotelier / Partner / Investor / Other), timestamp and consent status.</li>
            <li><strong>Preview access:</strong> IP address and user-agent for security. The submitted access code is <em>never</em> logged.</li>
            <li><strong>Server logs:</strong> technical metadata (IP, time, status codes) for security.</li>
          </ul>
          <H2>Newsletter and double opt-in</H2>
          <p>
            When you subscribe to the Launch List, we send a confirmation email. You must confirm with one click (double opt-in) before we activate the subscription.
            Unconfirmed pending subscriptions are deleted after up to 90 days.
          </p>
          <H2>Processors</H2>
          <ul className="list-disc space-y-2 pl-5">
            <li><strong>Resend</strong> — email delivery for transactional and marketing mail. They receive your email, name and the message content. See <a href="https://resend.com/privacy" className="text-bht-gold" target="_blank" rel="noreferrer">resend.com/privacy</a>.</li>
            <li><strong>MongoDB / hosting</strong> — encrypted storage of form data within the EU.</li>
          </ul>
          <H2>Retention</H2>
          <ul className="list-disc space-y-2 pl-5">
            <li>Contact & demo submissions: 24 months from last contact.</li>
            <li>Confirmed newsletter subscribers: until you unsubscribe.</li>
            <li>Pending (unconfirmed) subscriptions: 90 days.</li>
            <li>Preview audit logs: 12 months.</li>
          </ul>
          <H2>Your rights</H2>
          <p>
            You have the right to access, rectify, erase, restrict and port your personal data under the GDPR. Send requests to{" "}
            <a href="mailto:info@bhtsolutions.nl" className="text-bht-gold">info@bhtsolutions.nl</a>. We respond within 30 days.
            You may also lodge a complaint with the Dutch Data Protection Authority.
          </p>
          <H2>Cookies</H2>
          <p>
            We only use strictly-necessary functional cookies, including a session cookie for preview access. See our{" "}
            <Link to={`/${i18n.language}/legal/cookies`} className="text-bht-gold">Cookie Policy</Link>.
          </p>
          <H2>International transfers</H2>
          <p>
            Our primary hosting is in the EU. Our email processor Resend may process data outside the EU under Standard Contractual Clauses (SCCs) approved by the European Commission.
          </p>
          <H2>Analytics</H2>
          <p>
            We do not currently run analytics tracking on these pages. If we enable analytics, we will update this policy.
          </p>
        </>
      )}
    </LegalShell>
  );
}

// ---------- TERMS ----------
export function Terms() {
  const { i18n } = useTranslation();
  const isNl = i18n.language === "nl";
  return (
    <LegalShell title={isNl ? "Algemene voorwaarden" : "Terms & Conditions"} updated="2026-02-01" testId="page-terms">
      {isNl ? (
        <>
          <p>
            Deze voorwaarden regelen het gebruik van de website van BHT Solutions. Door de website te gebruiken, stemt u in met deze voorwaarden.
          </p>
          <H2>Doel van de website</H2>
          <p>
            Deze website is een informatieve en marketingwebsite over BHT One, het Hospitality Operating System dat wij ontwikkelen.
            De website vormt geen aanbod of contractuele verbintenis. Prijzen, functies en tijdlijnen kunnen veranderen.
          </p>
          <H2>Intellectueel eigendom</H2>
          <p>
            Alle content op deze website — tekst, ontwerp, illustraties, code en merken — is eigendom van BHT Solutions of haar licentiegevers.
            Zonder voorafgaande schriftelijke toestemming mag u niets kopiëren, distribueren of hergebruiken buiten normale browsergebruik.
          </p>
          <H2>Toegestaan gebruik</H2>
          <ul className="list-disc space-y-2 pl-5">
            <li>Persoonlijk, niet-commercieel bekijken van content.</li>
            <li>Formulieren invullen met eigen, juiste en actuele gegevens.</li>
            <li>Geen scraping, geautomatiseerd verzoek-verkeer of pogingen om beveiligingsmaatregelen te omzeilen.</li>
          </ul>
          <H2>Preview-toegang</H2>
          <p>
            Tijdens de stealth-launchperiode kan een deel van de website alleen toegankelijk zijn met een geldige toegangscode.
            Toegangscodes zijn strikt vertrouwelijk en worden alleen verstrekt aan geautoriseerde BHT-teamleden en genodigden.
            Delen of publiceren van toegangscodes is niet toegestaan en kan leiden tot intrekking van de toegang.
          </p>
          <H2>Beperking van aansprakelijkheid</H2>
          <p>
            De website wordt "as is" aangeboden. BHT Solutions is niet aansprakelijk voor eventuele directe of indirecte schade voortvloeiend uit het gebruik van deze website,
            behoudens in geval van opzet of grove nalatigheid, en tot het bij wet toegestane maximum.
          </p>
          <H2>Toepasselijk recht</H2>
          <p>
            Op deze voorwaarden is Nederlands recht van toepassing. Geschillen worden voorgelegd aan de bevoegde rechter te Amsterdam.
          </p>
          <H2>Wijzigingen</H2>
          <p>
            Wij kunnen deze voorwaarden aanpassen. De actuele versie is altijd via deze pagina beschikbaar.
          </p>
        </>
      ) : (
        <>
          <p>
            These terms govern the use of the BHT Solutions website. By using the site you agree to these terms.
          </p>
          <H2>Purpose of the website</H2>
          <p>
            This website is an informational and marketing site about BHT One, the Hospitality Operating System we are building.
            It does not constitute an offer or contract. Pricing, features and timelines may change.
          </p>
          <H2>Intellectual property</H2>
          <p>
            All content on this site — text, design, illustrations, code and marks — is owned by BHT Solutions or its licensors.
            You may not copy, distribute or reuse it beyond normal browser rendering without prior written consent.
          </p>
          <H2>Acceptable use</H2>
          <ul className="list-disc space-y-2 pl-5">
            <li>Personal, non-commercial viewing of content.</li>
            <li>Submitting forms with your own accurate, current data.</li>
            <li>No scraping, automated traffic or attempts to bypass security controls.</li>
          </ul>
          <H2>Preview access</H2>
          <p>
            During the stealth-launch period, parts of the website may only be accessible with a valid access code.
            Access codes are strictly confidential and issued only to authorised BHT team members and invited guests.
            Sharing or publishing access codes is not permitted and may result in access being revoked.
          </p>
          <H2>Limitation of liability</H2>
          <p>
            The website is provided "as is". BHT Solutions is not liable for any direct or indirect damages arising from your use of this site,
            except in cases of intent or gross negligence, and up to the maximum permitted by law.
          </p>
          <H2>Governing law</H2>
          <p>
            Dutch law applies. Disputes are subject to the exclusive jurisdiction of the courts of Amsterdam.
          </p>
          <H2>Changes</H2>
          <p>
            We may update these terms. The latest version is always available on this page.
          </p>
        </>
      )}
    </LegalShell>
  );
}

// ---------- COOKIES ----------
export function Cookies() {
  const { i18n } = useTranslation();
  const isNl = i18n.language === "nl";
  return (
    <LegalShell title={isNl ? "Cookiebeleid" : "Cookie Policy"} updated="2026-02-01" testId="page-cookies">
      {isNl ? (
        <>
          <p>
            Wij plaatsen alleen strikt noodzakelijke functionele cookies. Wij plaatsen geen tracking-, analytics- of marketing-cookies zonder uw toestemming.
          </p>
          <H2>Welke cookies wij plaatsen</H2>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>bht_preview_session</strong> — een versleutelde, HttpOnly sessie-cookie voor de preview-toegang. Levensduur: maximaal 8 uur.
              Bevat geen persoonsgegevens en geen toegangscode.
            </li>
            <li>
              <strong>Cloudflare / infrastructuur</strong> — technische cookies van onze CDN/hosting voor beveiliging en bot-preventie.
            </li>
          </ul>
          <H2>Waarom deze cookies noodzakelijk zijn</H2>
          <p>
            Zonder deze cookies kunnen wij geen veilige preview-toegang bieden en kan onze infrastructuur ons niet tegen misbruik beschermen.
            Onder de e-Privacy-richtlijn en AVG zijn strikt noodzakelijke cookies uitgezonderd van toestemmingsverplichting.
          </p>
          <H2>Cookies beheren</H2>
          <p>
            U kunt cookies te allen tijde blokkeren of verwijderen via uw browserinstellingen. Het uitschakelen van de sessie-cookie
            maakt preview-toegang onmogelijk. Dit heeft geen invloed op reguliere bezoekers.
          </p>
          <H2>Wijzigingen</H2>
          <p>
            Wij updaten dit beleid zodra wij nieuwe categorieën cookies introduceren of analytics inschakelen. Op dat moment vragen wij expliciet uw toestemming.
          </p>
        </>
      ) : (
        <>
          <p>
            We only place strictly-necessary functional cookies. We do not set tracking, analytics or marketing cookies without your consent.
          </p>
          <H2>Cookies we set</H2>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>bht_preview_session</strong> — a signed, HttpOnly session cookie used for preview access. Lifespan: up to 8 hours.
              It contains no personal data and never the access code itself.
            </li>
            <li>
              <strong>Cloudflare / infrastructure</strong> — technical cookies from our CDN/hosting used for security and bot mitigation.
            </li>
          </ul>
          <H2>Why these cookies are strictly necessary</H2>
          <p>
            Without these cookies we cannot provide secure preview access and our infrastructure cannot protect against abuse.
            Under the ePrivacy Directive and GDPR, strictly-necessary cookies are exempt from consent requirements.
          </p>
          <H2>Managing cookies</H2>
          <p>
            You can block or remove cookies via your browser settings at any time. Disabling the session cookie will prevent preview access.
            It does not affect regular visitors.
          </p>
          <H2>Changes</H2>
          <p>
            We will update this policy if we introduce new cookie categories or enable analytics. In that event, we will ask for your explicit consent.
          </p>
        </>
      )}
    </LegalShell>
  );
}
